import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as chargerConfigurationActions from './charger.configuration.store.actions';
import * as connectorActions from '../../../connectors/store/connector.actions';
import * as chargerConfigurationSelectors from './charger.configuration.store.selectors';
import { ChargerService } from '../../services/charger.service';
import { MessageTranslateService } from '../../../../shared/services/message.service';
import { Store } from '@ngrx/store';
import {
  catchError,
  concatMap,
  filter,
  map,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { ErrorUtil } from '../../../../shared/utils/error.util';

export class ChargerConfigurationEffects {
  actions$: Actions = inject(Actions);
  chargerService: ChargerService = inject(ChargerService);
  store: Store = inject(Store);
  messageService = inject(MessageTranslateService);

  loadChargerConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chargerConfigurationActions.loadChargerConfigurationDetails),
      withLatestFrom(
        this.store.select(
          chargerConfigurationSelectors.selectChargerConfiguration
        ),
        this.store.select(
          chargerConfigurationSelectors.selectDeviceConfiguration
        )
      ),
      switchMap(
        ([requestData, currentChargerConfiguration, currentDeviceConfig]) => {
          if (
            requestData.force ||
            requestData.chargerId !== currentChargerConfiguration?.chargerId
          ) {
            console.log('loading charger configuration');
            return this.chargerService
              .getChargerConfigurations(requestData.chargerId)
              .pipe(
                concatMap((chargerConfig) => {
                  if (chargerConfig.externalChargerId) {
                    return this.chargerService
                      .getDeviceConfiguration(chargerConfig.externalChargerId)
                      .pipe(
                        map((deviceConfig) =>
                          chargerConfigurationActions.loadChargerConfigurationDetailsSuccess(
                            {
                              chargerConfiguration: chargerConfig,
                              deviceConfig: deviceConfig,
                            }
                          )
                        )
                      );
                  }
                  return of(
                    chargerConfigurationActions.loadChargerConfigurationDetailsSuccess(
                      {
                        chargerConfiguration: chargerConfig,
                        deviceConfig: {
                          chargerId: chargerConfig.externalChargerId,
                          configurationKey: [],
                        },
                      }
                    )
                  );
                })
              );
          } else {
            console.log('loading existing one');
            return of(
              chargerConfigurationActions.loadChargerConfigurationDetailsSuccess(
                {
                  chargerConfiguration: currentChargerConfiguration,
                  deviceConfig: currentDeviceConfig,
                }
              )
            );
          }
        }
      )
    )
  );

  saveExternalCetnralSystemConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        chargerConfigurationActions.saveExternalCentralSystemConfiguration
      ),
      switchMap((requestData) => {
        return this.chargerService
          .saveExternalCentralSystem(
            requestData.chargerId,
            requestData.externalCentralSystem
          )
          .pipe(
            map((res) => {
              if (res.isSuccess) {
                const externalCentralSystem = {
                  ...requestData.externalCentralSystem,
                };
                return chargerConfigurationActions.saveExternalCentralSystemConfigurationSuccess(
                  { data: externalCentralSystem }
                );
              }
              return chargerConfigurationActions.saveExternalCentralSystemConfigurationFailure(
                { error: res.error! }
              );
            })
          );
      })
    )
  );

  saveChargerDeviceDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chargerConfigurationActions.saveChargerDeviceDetails),
      switchMap((requestData) => {
        return this.chargerService
          .saveDeviceDetails(requestData.chargerId, requestData.deviceDetails)
          .pipe(
            map((res) => {
              if (res.isSuccess) {
                const deviceDetails = { ...requestData.deviceDetails };
                return chargerConfigurationActions.saveChargerDeviceDetailsSuccess(
                  { deviceDetails }
                );
              }
              return chargerConfigurationActions.saveChargerDeviceDetailsFailure(
                { error: res.error! }
              );
            })
          );
      })
    )
  );

  requestConfigurationReload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chargerConfigurationActions.reloadFullConfiguration),
      filter((requestData) => requestData.chargerId != null),
      switchMap((requestData) =>
        this.chargerService
          .requestConfigurationReload(requestData.chargerId)
          .pipe(
            map((res) => {
              if (res.isSuccess) {
                return chargerConfigurationActions.realodFullConfigurationRequestPending(
                  { requestId: res.value }
                );
              } else {
                return chargerConfigurationActions.reloadFullConfigurationFailure(
                  { error: res.error! }
                );
              }
            }),
            catchError((err) =>
              of(
                chargerConfigurationActions.reloadFullConfigurationFailure({
                  error: ErrorUtil.convertToErrorModel(err),
                })
              )
            )
          )
      )
    )
  );

  saveConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chargerConfigurationActions.saveConfiguration),
      switchMap((requestData) =>
        this.chargerService
          .saveConfiguration(requestData.chargerId, requestData.config)
          .pipe(
            map((res) => {
              if (res.isSuccess) {
                return chargerConfigurationActions.saveConfigurationPending({
                  requestId: res.value,
                });
              } else {
                return chargerConfigurationActions.saveConfigurationFailure({
                  error: res.error!,
                });
              }
            }),
            catchError((err) =>
              of(
                chargerConfigurationActions.saveConfigurationFailure({
                  error: ErrorUtil.convertToErrorModel(err),
                })
              )
            )
          )
      )
    )
  );

  saveAuthTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chargerConfigurationActions.saveAuthType),
      switchMap((requestData) => {
        return this.chargerService
          .saveAuthType(requestData.chargerId, requestData.authType)
          .pipe(
            map((res) => {
              if (res.isSuccess) {
                const authType = { ...requestData.authType };
                return chargerConfigurationActions.saveAuthTypeSuccess({
                  authType,
                });
              }
              return chargerConfigurationActions.saveAuthTypeFailure({
                error: res.error!,
              });
            })
          );
      })
    )
  );

  loadDeviceConfigurationFromDb$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chargerConfigurationActions.loadDeviceConfigurationFromDb),
      switchMap((requestData) =>
        this.chargerService
          .getDeviceConfiguration(requestData.externalChargerId)
          .pipe(
            map((response) =>
              chargerConfigurationActions.loadDeviceConfigurationFromDbSuccess({
                deviceConfiguration: response,
              })
            ),
            catchError((err) =>
              of(
                chargerConfigurationActions.loadDeviceConfigurationFromDbFailure(
                  { error: ErrorUtil.convertToErrorModel(err) }
                )
              )
            )
          )
      )
    )
  );

  syncConnectorMaxChargingCapacity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chargerConfigurationActions.saveChargerDeviceDetailsSuccess),
      map(({ deviceDetails }) =>
        connectorActions.updateConnectorMaxChargingCapacity({
          maxChargingCapacity: deviceDetails.chargingPower,
        })
      )
    )
  );

  showErrorForChargers$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          chargerConfigurationActions.loadChargerConfigurationDetailsFailure,
          chargerConfigurationActions.saveAuthTypeFailure,
          chargerConfigurationActions.saveChargerDeviceDetailsFailure,
          chargerConfigurationActions.saveExternalCentralSystemConfigurationFailure,
          chargerConfigurationActions.reloadFullConfigurationFailure,
          chargerConfigurationActions.saveConfigurationFailure
        ),
        tap((err) => {
          console.log('CAUGHT ERROR:', err);
          this.messageService.error(err.error);
        })
      ),
    { dispatch: false }
  );
}
